import React, { useState } from "react";
import { Bell, BellOff, Check, ExternalLink, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { useNotifications } from "../context/NotificationsContext";

const formatRelative = (iso) => {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function NotificationBell() {
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    remove,
    browserPushEnabled,
    enableBrowserPush,
  } = useNotifications();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          className="relative p-2 rounded-full hover:bg-[#EAE6E1]/60 transition-soft text-[#2A2F2B]"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C66B59] text-white text-[10px] font-mono-ticker font-semibold flex items-center justify-center"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 rounded-2xl border-[#EAE6E1] shadow-lg"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAE6E1]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Alerts
            </div>
            <div className="text-sm font-medium text-[#2A2F2B]">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              data-testid="notification-mark-all-read"
              className="text-xs text-[#547A8F] hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>

        {!browserPushEnabled && (
          <button
            onClick={enableBrowserPush}
            data-testid="notification-enable-push"
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-[#547A8F] bg-[#547A8F]/5 border-b border-[#EAE6E1] hover:bg-[#547A8F]/10 transition-soft"
          >
            <BellOff className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>Enable browser notifications for desktop alerts</span>
          </button>
        )}

        <div className="max-h-[400px] overflow-y-auto" data-testid="notification-list">
          {notifications.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-[#6B7068]">
              No notifications yet. Set a price alert from any asset page.
            </div>
          )}
          {notifications.map((n) => (
            <div
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              className={`group flex items-start gap-3 px-4 py-3 border-b border-[#EAE6E1]/60 hover:bg-[#F9F8F6] transition-soft ${
                !n.is_read ? "bg-[#FFF8F1]/60" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <Link
                  to={`/asset/${n.asset_type}/${n.symbol}`}
                  onClick={() => {
                    if (!n.is_read) markRead(n.id);
                    setOpen(false);
                  }}
                  className="block"
                >
                  <div className="flex items-center gap-1.5">
                    {!n.is_read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C66B59]" />
                    )}
                    <span className="font-mono-ticker text-xs text-[#6B7068]">
                      {n.symbol}
                    </span>
                    <ExternalLink className="w-3 h-3 text-[#6B7068]" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-[#2A2F2B] mt-0.5 leading-snug">
                    {n.message}
                  </p>
                  <div className="text-[10px] text-[#6B7068] mt-1 font-mono-ticker">
                    {formatRelative(n.created_at)}
                  </div>
                </Link>
              </div>
              <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-soft">
                {!n.is_read && (
                  <button
                    onClick={() => markRead(n.id)}
                    data-testid={`notification-mark-read-${n.id}`}
                    className="p-1 rounded hover:bg-[#EAE6E1] text-[#6B7068]"
                    title="Mark read"
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
                <button
                  onClick={() => remove(n.id)}
                  data-testid={`notification-delete-${n.id}`}
                  className="p-1 rounded hover:bg-[#EAE6E1] text-[#6B7068]"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-2 border-t border-[#EAE6E1] bg-[#F9F8F6] rounded-b-2xl">
          <Link
            to="/alerts"
            onClick={() => setOpen(false)}
            data-testid="notification-manage-alerts"
            className="text-xs text-[#547A8F] hover:underline"
          >
            Manage alerts →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
